import { useEffect, useMemo, useState, type JSX, type ReactNode } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import {
  AlertCircle,
  ArrowLeft,
  Ban,
  CheckCircle2,
  Check,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  CircleDashed,
  Clock,
  Copy,
  Link2,
  Pencil,
  Send,
  UserRoundPen,
} from 'lucide-react';
import {
  Badge,
  type BadgeProps,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@caratuva/ui';
import { canTransition, type InvoiceStatus } from '@caratuva/shared-types';
import {
  ApiError,
  approveInvoice,
  cancelInvoice,
  getInvoice,
  getKybSubmission,
  getPayoutConfig,
  inviteBuyer,
  reassignInvoiceBuyer,
} from '../lib/api';

/*
 * Invoice detail.
 *
 * Renders a header card, line-item table, payment-event timeline, and an
 * action panel with a cancel button gated on the state-machine.
 *
 * The PaymentEvent DTO returned by `GET /v1/invoices/:id` does not currently
 * surface the raw `payload` — only `id`, `type`, `source`, `externalEventId`,
 * `occurredAt`. The UI is wired so that when the API starts including the
 * payload (a follow-up DTO change), the timeline rows will gracefully expand.
 */

export const Route = createFileRoute('/_app/invoices/$id/')({
  component: InvoiceDetail,
});

// Statuses where the seller has already invited the buyer but the buyer
// hasn't paid yet. In these states, /invite-buyer is idempotent on the
// backend — it skips the state transition and just redelivers the magic-
// link email. We mirror the set here so the UI can keep the action
// enabled (relabeled as "Resend invite email") instead of greying it out.
const RESEND_INVITE_STATES: ReadonlySet<InvoiceStatus> = new Set([
  'awaiting_buyer',
  'buyer_kyc_pending',
  'buyer_kyc_approved',
  'fx_quoted',
  'fx_expired',
]);

const DEFAULT_BUYER_PORTAL_URL = 'http://localhost:5174';

function buyerPortalUrl(): string {
  const fromEnv = import.meta.env.VITE_BUYER_PORTAL_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv;
  return DEFAULT_BUYER_PORTAL_URL;
}

const STATUS_VARIANT: Record<InvoiceStatus, BadgeProps['variant']> = {
  draft: 'secondary',
  awaiting_approval: 'warning',
  approved: 'info',
  awaiting_buyer: 'info',
  buyer_kyc_pending: 'warning',
  buyer_kyc_approved: 'info',
  buyer_kyc_rejected: 'danger',
  fx_quoted: 'info',
  fx_expired: 'warning',
  on_ramp_pending: 'warning',
  on_ramp_failed: 'danger',
  fiat_received: 'info',
  on_chain_confirmed: 'info',
  offramp_pending: 'warning',
  offramp_failed: 'danger',
  settled: 'success',
  partial_payment: 'warning',
  expired: 'secondary',
  cancelled: 'destructive',
  restored: 'info',
  manual_review: 'warning',
};

function InvoiceDetail(): JSX.Element {
  const intl = useIntl();
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['invoice', id],
    queryFn: ({ signal }) => getInvoice(id, signal),
  });

  // Duplicating an invoice is an invoice-creation path, so it follows the same
  // KYB + PIX gate as "New invoice" (shared query keys with the Payout page).
  const kybQuery = useQuery({
    queryKey: ['kyb-submission'],
    queryFn: ({ signal }) => getKybSubmission(signal),
  });
  const payoutQuery = useQuery({
    queryKey: ['payout-config'],
    queryFn: ({ signal }) => getPayoutConfig(signal),
  });
  const blockDuplicate =
    !kybQuery.isPending &&
    !payoutQuery.isPending &&
    !(kybQuery.data?.status === 'approved' && payoutQuery.data?.hasBankAccount === true);

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState<string | null>(null);

  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassignEmail, setReassignEmail] = useState('');
  const [reassignName, setReassignName] = useState('');
  const [reassignError, setReassignError] = useState<string | null>(null);

  function extractApiMessage(err: unknown, fallback: string): string {
    if (err instanceof ApiError) {
      const body = err.body as { message?: unknown } | null;
      return Array.isArray(body?.message)
        ? (body.message as string[]).join('; ')
        : typeof body?.message === 'string'
          ? body.message
          : err.message;
    }
    return err instanceof Error ? err.message : fallback;
  }

  const cancelMutation = useMutation({
    mutationFn: (reason: string) => cancelInvoice(id, reason),
    onSuccess: (data) => {
      queryClient.setQueryData(['invoice', id], data);
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setCancelDialogOpen(false);
      setCancelReason('');
      setCancelError(null);
    },
    onError: (err) => {
      setCancelError(
        extractApiMessage(
          err,
          intl.formatMessage({
            id: 'invoices.detail.cancel.error',
            defaultMessage: 'Failed to cancel invoice.',
          }),
        ),
      );
    },
  });

  const approveMutation = useMutation({
    mutationFn: () => approveInvoice(id),
    onSuccess: (data) => {
      queryClient.setQueryData(['invoice', id], data);
      void queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setApproveDialogOpen(false);
      setApproveError(null);
    },
    onError: (err) => {
      setApproveError(
        extractApiMessage(
          err,
          intl.formatMessage({
            id: 'invoices.detail.action.approve.error',
            defaultMessage: 'Failed to approve invoice.',
          }),
        ),
      );
    },
  });

  const inviteMutation = useMutation({
    mutationFn: () => inviteBuyer(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setInviteDialogOpen(false);
      setInviteError(null);
    },
    onError: (err) => {
      setInviteError(
        extractApiMessage(
          err,
          intl.formatMessage({
            id: 'invoices.detail.action.invite.error',
            defaultMessage: 'Failed to invite buyer.',
          }),
        ),
      );
    },
  });

  const reassignMutation = useMutation({
    mutationFn: ({ email, name }: { email: string; name: string }) =>
      reassignInvoiceBuyer(id, email, 'Buyer reassigned from invoice detail UI', name || undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setReassignDialogOpen(false);
      setReassignEmail('');
      setReassignName('');
      setReassignError(null);
    },
    onError: (err) => {
      setReassignError(extractApiMessage(err, 'Failed to reassign buyer.'));
    },
  });

  const invoice = query.data;
  const cancelEnabled = useMemo(
    () => (invoice ? canTransition(invoice.status as InvoiceStatus, 'cancelled') : false),
    [invoice],
  );
  const approveEnabled = useMemo(
    () => (invoice ? canTransition(invoice.status as InvoiceStatus, 'approved') : false),
    [invoice],
  );
  const editEnabled = useMemo(
    () => invoice?.status === 'draft' || invoice?.status === 'awaiting_approval',
    [invoice],
  );
  const reassignEnabled = useMemo(
    () =>
      invoice != null &&
      !editEnabled &&
      invoice.status !== 'settled' &&
      invoice.status !== 'cancelled',
    [invoice, editEnabled],
  );
  // Resend semantics: the action is enabled either for the first invite
  // (`approved -> buyer_invited`) or for any pre-payment status where the
  // backend will redeliver the magic-link without re-emitting the event.
  // Once payment is in progress (`on_ramp_*` and beyond) the link is no
  // longer useful and we grey the button out.
  const isResend = useMemo(
    () => (invoice ? RESEND_INVITE_STATES.has(invoice.status as InvoiceStatus) : false),
    [invoice],
  );
  const inviteEnabled = useMemo(() => {
    if (!invoice) return false;
    if (isResend) return true;
    return canTransition(invoice.status as InvoiceStatus, 'buyer_invited');
  }, [invoice, isResend]);
  const copyLinkEnabled = inviteEnabled;

  const [linkCopied, setLinkCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  useEffect(() => {
    if (!linkCopied) return;
    const t = window.setTimeout(() => setLinkCopied(false), 2000);
    return () => window.clearTimeout(t);
  }, [linkCopied]);
  const paymentLink = invoice ? `${buyerPortalUrl()}/r/${invoice.publicId}` : null;
  async function copyPaymentLink(): Promise<void> {
    if (!paymentLink) return;
    setCopyError(null);
    try {
      await navigator.clipboard.writeText(paymentLink);
      setLinkCopied(true);
    } catch {
      setCopyError(
        intl.formatMessage({
          id: 'invoices.detail.action.copyLink.error',
          defaultMessage: 'Could not copy link',
        }),
      );
    }
  }

  return (
    <section className="mx-auto max-w-5xl space-y-6">
      <Link
        to="/invoices"
        className="inline-flex items-center gap-1 text-sm text-navy-700 transition-colors hover:text-navy-900"
      >
        <ArrowLeft aria-hidden className="h-4 w-4" strokeWidth={1.5} />
        {intl.formatMessage({ id: 'nav.invoices', defaultMessage: 'Invoices' })}
      </Link>

      {query.isPending ? (
        <p className="text-sm text-navy-600">
          {intl.formatMessage({
            id: 'invoices.detail.loading',
            defaultMessage: 'Loading invoice...',
          })}
        </p>
      ) : query.isError || !invoice ? (
        <p className="text-sm text-[var(--color-danger)]">
          {intl.formatMessage({
            id: 'invoices.detail.error',
            defaultMessage: 'Failed to load invoice.',
          })}
        </p>
      ) : (
        <>
          <div className="border-b border-navy-100 pb-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="space-y-1">
                <p className="font-mono text-xs text-navy-600">{invoice.publicId}</p>
                <h1 className="text-2xl font-semibold tracking-tight text-navy-900">
                  {intl.formatMessage({
                    id: 'invoices.detail.title',
                    defaultMessage: 'Invoice',
                  })}
                </h1>
              </div>
              <Badge
                variant={STATUS_VARIANT[invoice.status as InvoiceStatus] ?? 'secondary'}
                className="font-mono text-[11px] uppercase tracking-wider"
              >
                {invoice.status}
              </Badge>
            </div>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              <DetailField
                label={intl.formatMessage({
                  id: 'invoices.detail.field.buyer',
                  defaultMessage: 'Buyer',
                })}
              >
                {invoice.buyerName ? (
                  <span className="block truncate" title={invoice.buyerName}>
                    {invoice.buyerName}
                  </span>
                ) : invoice.buyerEmail ? (
                  <span className="block truncate" title={invoice.buyerEmail}>
                    {invoice.buyerEmail}
                  </span>
                ) : (
                  <span className="block truncate font-mono text-xs" title={invoice.buyerId}>
                    {invoice.buyerId}
                  </span>
                )}
              </DetailField>
              {invoice.buyerEmail && invoice.buyerName ? (
                <DetailField
                  label={intl.formatMessage({
                    id: 'invoices.detail.field.buyerEmail',
                    defaultMessage: 'Buyer email',
                  })}
                >
                  <span className="block truncate" title={invoice.buyerEmail}>
                    {invoice.buyerEmail}
                  </span>
                </DetailField>
              ) : null}
              <DetailField
                label={intl.formatMessage({
                  id: 'invoices.detail.field.seller',
                  defaultMessage: 'Seller',
                })}
              >
                {invoice.sellerName ? (
                  <span className="block truncate" title={invoice.sellerName}>
                    {invoice.sellerName}
                  </span>
                ) : invoice.sellerExternalId ? (
                  <span
                    className="block truncate font-mono text-xs"
                    title={invoice.sellerExternalId}
                  >
                    {invoice.sellerExternalId}
                  </span>
                ) : (
                  <span className="text-navy-500">—</span>
                )}
              </DetailField>
              <DetailField
                label={intl.formatMessage({
                  id: 'invoices.detail.field.total',
                  defaultMessage: 'Total',
                })}
              >
                <span className="font-mono tabular-nums font-medium">{invoice.amount}</span>
                <span className="ml-1 text-navy-700">{invoice.currency}</span>
              </DetailField>
              <DetailField
                label={intl.formatMessage({
                  id: 'invoices.detail.field.createdAt',
                  defaultMessage: 'Created',
                })}
              >
                {formatDateTime(invoice.createdAt, intl.locale)}
              </DetailField>
              <DetailField
                label={intl.formatMessage({
                  id: 'invoices.detail.field.dueDate',
                  defaultMessage: 'Due date',
                })}
              >
                {invoice.dueDate ? formatDate(invoice.dueDate, intl.locale) : '—'}
              </DetailField>
              {invoice.buyerRef ? (
                <DetailField
                  label={intl.formatMessage({
                    id: 'invoices.detail.field.buyerRef',
                    defaultMessage: 'Buyer ref',
                  })}
                >
                  {invoice.buyerRef}
                </DetailField>
              ) : null}
              {invoice.dueNumber ? (
                <DetailField
                  label={intl.formatMessage({
                    id: 'invoices.detail.field.dueNumber',
                    defaultMessage: 'DU-E',
                  })}
                >
                  {invoice.dueNumber}
                </DetailField>
              ) : null}
              {invoice.exportContractRef ? (
                <DetailField
                  label={intl.formatMessage({
                    id: 'invoices.detail.field.exportContractRef',
                    defaultMessage: 'Export contract',
                  })}
                >
                  {invoice.exportContractRef}
                </DetailField>
              ) : null}
              {invoice.incoterm ? (
                <DetailField
                  label={intl.formatMessage({
                    id: 'invoices.detail.field.incoterm',
                    defaultMessage: 'Incoterm',
                  })}
                >
                  {invoice.incoterm}
                </DetailField>
              ) : null}
              {invoice.portOfLoading ? (
                <DetailField
                  label={intl.formatMessage({
                    id: 'invoices.detail.field.portOfLoading',
                    defaultMessage: 'Port of loading',
                  })}
                >
                  {invoice.portOfLoading}
                </DetailField>
              ) : null}
              {invoice.cancelReason ? (
                <DetailField
                  label={intl.formatMessage({
                    id: 'invoices.detail.field.cancelReason',
                    defaultMessage: 'Cancel reason',
                  })}
                >
                  {invoice.cancelReason}
                </DetailField>
              ) : null}
            </div>
          </div>

          <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_280px]">
            <div className="space-y-8">
              <section>
                <header className="mb-4 space-y-1">
                  <h2 className="text-base font-semibold text-navy-900">
                    {intl.formatMessage({
                      id: 'invoices.detail.section.lineItems',
                      defaultMessage: 'Line items',
                    })}
                  </h2>
                </header>
                <Card className="overflow-hidden border-navy-100 shadow-xs">
                  <CardContent className="p-0">
                    {invoice.lineItems && invoice.lineItems.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow className="border-navy-100 hover:bg-transparent">
                            <TableHead className="h-11 pl-6 text-[11px] font-medium uppercase tracking-wider text-navy-700">
                              {intl.formatMessage({
                                id: 'invoices.detail.column.description',
                                defaultMessage: 'Description',
                              })}
                            </TableHead>
                            <TableHead className="h-11 text-right text-[11px] font-medium uppercase tracking-wider text-navy-700">
                              {intl.formatMessage({
                                id: 'invoices.detail.column.quantity',
                                defaultMessage: 'Qty',
                              })}
                            </TableHead>
                            <TableHead className="h-11 text-right text-[11px] font-medium uppercase tracking-wider text-navy-700">
                              {intl.formatMessage({
                                id: 'invoices.detail.column.unitPrice',
                                defaultMessage: 'Unit price',
                              })}
                            </TableHead>
                            <TableHead className="h-11 pr-6 text-right text-[11px] font-medium uppercase tracking-wider text-navy-700">
                              {intl.formatMessage({
                                id: 'invoices.detail.column.subtotal',
                                defaultMessage: 'Subtotal',
                              })}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {invoice.lineItems.map((li) => (
                            <TableRow key={li.id} className="border-navy-100 hover:bg-beige-50/40">
                              <TableCell className="py-3 pl-6 text-navy-900">
                                {li.description}
                              </TableCell>
                              <TableCell className="py-3 text-right tabular-nums text-navy-900">
                                {li.quantity}
                              </TableCell>
                              <TableCell className="py-3 text-right tabular-nums text-navy-900">
                                {li.unitPrice}
                              </TableCell>
                              <TableCell className="py-3 pr-6 text-right font-medium tabular-nums text-navy-900">
                                {li.subtotal}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <p className="px-6 py-4 text-sm text-navy-600">
                        {intl.formatMessage({
                          id: 'invoices.detail.lineItems.empty',
                          defaultMessage: 'No line items.',
                        })}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </section>

              <section>
                <header className="mb-4 space-y-1">
                  <h2 className="text-base font-semibold text-navy-900">
                    {intl.formatMessage({
                      id: 'invoices.detail.section.timeline',
                      defaultMessage: 'Payment timeline',
                    })}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {intl.formatMessage({
                      id: 'invoices.detail.section.timeline.description',
                      defaultMessage: 'Events recorded across the invoice lifecycle.',
                    })}
                  </p>
                </header>
                <Card className="border-navy-100 shadow-xs">
                  <CardContent className="p-6">
                    <Timeline events={invoice.timeline} />
                  </CardContent>
                </Card>
              </section>
            </div>

            <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
              <section>
                <header className="mb-4 space-y-1">
                  <h2 className="text-base font-semibold text-navy-900">
                    {intl.formatMessage({
                      id: 'invoices.detail.actions.title',
                      defaultMessage: 'Actions',
                    })}
                  </h2>
                </header>
                <Card className="border-navy-100 shadow-xs">
                  <CardContent className="space-y-3 p-4">
                    {editEnabled ? (
                      <Button asChild variant="outline" className="w-full">
                        <Link to="/invoices/$id/edit" params={{ id }}>
                          <Pencil aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                          Edit invoice
                        </Link>
                      </Button>
                    ) : (
                      <Button variant="outline" className="w-full" disabled>
                        <Pencil aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                        Edit invoice
                      </Button>
                    )}

                    <Dialog
                      open={approveDialogOpen}
                      onOpenChange={(next) => {
                        setApproveDialogOpen(next);
                        if (!next) {
                          setApproveError(null);
                        }
                      }}
                    >
                      <DialogTrigger asChild>
                        <Button
                          variant="default"
                          className="w-full"
                          disabled={!approveEnabled}
                          title={
                            approveEnabled
                              ? undefined
                              : intl.formatMessage({
                                  id: 'invoices.detail.action.approve.disabledHint',
                                  defaultMessage:
                                    'Invoice cannot be approved in its current state.',
                                })
                          }
                        >
                          <CheckCircle2 aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                          {intl.formatMessage({
                            id: 'invoices.detail.action.approve.button',
                            defaultMessage: 'Approve invoice',
                          })}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>
                            {intl.formatMessage({
                              id: 'invoices.detail.action.approve.dialog.title',
                              defaultMessage: 'Approve this invoice?',
                            })}
                          </DialogTitle>
                          <DialogDescription>
                            {intl.formatMessage({
                              id: 'invoices.detail.action.approve.dialog.description',
                              defaultMessage:
                                'Approving moves the invoice from draft to approved. You can still cancel it before the buyer is invited.',
                            })}
                          </DialogDescription>
                        </DialogHeader>
                        {approveError ? (
                          <p role="alert" className="text-xs text-[var(--color-danger)]">
                            {approveError}
                          </p>
                        ) : null}
                        <DialogFooter>
                          <DialogClose asChild>
                            <Button variant="ghost">
                              {intl.formatMessage({
                                id: 'invoices.detail.action.approve.dialog.dismiss',
                                defaultMessage: 'Not yet',
                              })}
                            </Button>
                          </DialogClose>
                          <Button
                            variant="default"
                            disabled={approveMutation.isPending}
                            onClick={() => {
                              setApproveError(null);
                              approveMutation.mutate();
                            }}
                          >
                            {approveMutation.isPending
                              ? intl.formatMessage({
                                  id: 'invoices.detail.action.approve.dialog.submitting',
                                  defaultMessage: 'Approving...',
                                })
                              : intl.formatMessage({
                                  id: 'invoices.detail.action.approve.dialog.confirm',
                                  defaultMessage: 'Approve invoice',
                                })}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>

                    <Dialog
                      open={inviteDialogOpen}
                      onOpenChange={(next) => {
                        setInviteDialogOpen(next);
                        if (!next) {
                          setInviteError(null);
                        }
                      }}
                    >
                      <DialogTrigger asChild>
                        <Button
                          variant="default"
                          className="w-full"
                          disabled={!inviteEnabled}
                          title={
                            inviteEnabled
                              ? undefined
                              : intl.formatMessage({
                                  id: isResend
                                    ? 'invoices.detail.action.invite.disabledHint.paid'
                                    : 'invoices.detail.action.invite.disabledHint',
                                  defaultMessage: isResend
                                    ? 'Buyer has already paid; the invite is no longer needed.'
                                    : 'Invoice must be approved before inviting the buyer.',
                                })
                          }
                        >
                          <Send aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                          {intl.formatMessage({
                            id: isResend
                              ? 'invoices.detail.action.invite.button.resend'
                              : 'invoices.detail.action.invite.button',
                            defaultMessage: isResend ? 'Resend invite email' : 'Invite buyer',
                          })}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>
                            {intl.formatMessage({
                              id: isResend
                                ? 'invoices.detail.action.invite.dialog.title.resend'
                                : 'invoices.detail.action.invite.dialog.title',
                              defaultMessage: isResend
                                ? 'Resend the invite email?'
                                : 'Invite the buyer?',
                            })}
                          </DialogTitle>
                          <DialogDescription>
                            {intl.formatMessage({
                              id: isResend
                                ? 'invoices.detail.action.invite.dialog.description.resend'
                                : 'invoices.detail.action.invite.dialog.description',
                              defaultMessage: isResend
                                ? 'We will email another magic-link to the buyer below in case the previous email was missed.'
                                : 'We will email a magic-link sign-in to the buyer below so they can complete KYC and pay.',
                            })}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-2">
                          <Label htmlFor="inviteBuyerEmail">
                            {intl.formatMessage({
                              id: 'invoices.detail.action.invite.dialog.emailLabel',
                              defaultMessage: 'Buyer email',
                            })}
                          </Label>
                          <Input id="inviteBuyerEmail" value={invoice.buyerEmail ?? ''} readOnly />
                          {inviteError ? (
                            <p role="alert" className="text-xs text-[var(--color-danger)]">
                              {inviteError}
                            </p>
                          ) : null}
                        </div>
                        <DialogFooter>
                          <DialogClose asChild>
                            <Button variant="ghost">
                              {intl.formatMessage({
                                id: 'invoices.detail.action.invite.dialog.dismiss',
                                defaultMessage: 'Not yet',
                              })}
                            </Button>
                          </DialogClose>
                          <Button
                            variant="default"
                            disabled={inviteMutation.isPending}
                            onClick={() => {
                              setInviteError(null);
                              inviteMutation.mutate();
                            }}
                          >
                            {inviteMutation.isPending
                              ? intl.formatMessage({
                                  id: 'invoices.detail.action.invite.dialog.submitting',
                                  defaultMessage: 'Sending...',
                                })
                              : intl.formatMessage({
                                  id: isResend
                                    ? 'invoices.detail.action.invite.dialog.confirm.resend'
                                    : 'invoices.detail.action.invite.dialog.confirm',
                                  defaultMessage: isResend ? 'Resend email' : 'Send magic-link',
                                })}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>

                    <div className="space-y-1">
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        disabled={!copyLinkEnabled}
                        onClick={copyPaymentLink}
                        title={
                          copyLinkEnabled
                            ? (paymentLink ?? undefined)
                            : intl.formatMessage({
                                id: 'invoices.detail.action.copyLink.disabledHint',
                                defaultMessage:
                                  'Buyer has already paid; the payment link is no longer relevant.',
                              })
                        }
                      >
                        {linkCopied ? (
                          <Check
                            aria-hidden
                            className="h-4 w-4 text-[var(--color-success)]"
                            strokeWidth={1.5}
                          />
                        ) : (
                          <Link2 aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                        )}
                        {linkCopied
                          ? intl.formatMessage({
                              id: 'invoices.detail.action.copyLink.copied',
                              defaultMessage: 'Link copied',
                            })
                          : intl.formatMessage({
                              id: 'invoices.detail.action.copyLink.button',
                              defaultMessage: 'Copy payment link',
                            })}
                      </Button>
                      {copyError ? (
                        <p role="alert" className="text-xs text-[var(--color-danger)]">
                          {copyError}
                        </p>
                      ) : copyLinkEnabled ? (
                        <p className="text-xs text-navy-600">
                          {intl.formatMessage({
                            id: 'invoices.detail.action.copyLink.hint',
                            defaultMessage:
                              'Share this link directly with the buyer if they did not receive the email.',
                          })}
                        </p>
                      ) : null}
                    </div>

                    <Dialog
                      open={reassignDialogOpen}
                      onOpenChange={(next) => {
                        setReassignDialogOpen(next);
                        if (!next) {
                          setReassignEmail('');
                          setReassignName('');
                          setReassignError(null);
                        }
                      }}
                    >
                      <DialogTrigger asChild>
                        <Button variant="outline" className="w-full" disabled={!reassignEnabled}>
                          <UserRoundPen aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                          Reassign buyer
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Reassign buyer</DialogTitle>
                          <DialogDescription>
                            Change the buyer on this invoice. Useful when a KYC flow is stuck — the
                            new buyer starts with a clean state.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="reassignEmail">New buyer email</Label>
                            <Input
                              id="reassignEmail"
                              type="email"
                              value={reassignEmail}
                              onChange={(e) => setReassignEmail(e.target.value)}
                              placeholder="buyer@example.com"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="reassignName">
                              Buyer name <span className="text-muted-foreground">(optional)</span>
                            </Label>
                            <Input
                              id="reassignName"
                              value={reassignName}
                              onChange={(e) => setReassignName(e.target.value)}
                              placeholder="Full name"
                            />
                          </div>
                          {reassignError ? (
                            <p role="alert" className="text-xs text-[var(--color-danger)]">
                              {reassignError}
                            </p>
                          ) : null}
                        </div>
                        <DialogFooter>
                          <DialogClose asChild>
                            <Button variant="ghost">Cancel</Button>
                          </DialogClose>
                          <Button
                            disabled={!reassignEmail.trim() || reassignMutation.isPending}
                            onClick={() => {
                              setReassignError(null);
                              reassignMutation.mutate({
                                email: reassignEmail.trim(),
                                name: reassignName.trim(),
                              });
                            }}
                          >
                            {reassignMutation.isPending ? 'Reassigning…' : 'Reassign buyer'}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>

                    <Dialog
                      open={cancelDialogOpen}
                      onOpenChange={(next) => {
                        setCancelDialogOpen(next);
                        if (!next) {
                          setCancelError(null);
                        }
                      }}
                    >
                      <DialogTrigger asChild>
                        <Button
                          variant="destructive"
                          className="w-full"
                          disabled={!cancelEnabled}
                          title={
                            cancelEnabled
                              ? undefined
                              : intl.formatMessage({
                                  id: 'invoices.detail.actions.cancel.disabledHint',
                                  defaultMessage:
                                    'Invoice cannot be cancelled in its current state.',
                                })
                          }
                        >
                          <Ban aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                          {intl.formatMessage({
                            id: 'invoices.detail.actions.cancel',
                            defaultMessage: 'Cancel invoice',
                          })}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>
                            {intl.formatMessage({
                              id: 'invoices.detail.cancel.title',
                              defaultMessage: 'Cancel invoice?',
                            })}
                          </DialogTitle>
                          <DialogDescription>
                            {intl.formatMessage({
                              id: 'invoices.detail.cancel.description',
                              defaultMessage:
                                'This stops the invoice from progressing. Tell the buyer (and your audit log) why.',
                            })}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-2">
                          <Label htmlFor="cancelReason">
                            {intl.formatMessage({
                              id: 'invoices.detail.cancel.reason',
                              defaultMessage: 'Reason',
                            })}
                          </Label>
                          <Input
                            id="cancelReason"
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            maxLength={500}
                            placeholder={intl.formatMessage({
                              id: 'invoices.detail.cancel.reason.placeholder',
                              defaultMessage: 'e.g. Buyer requested termination',
                            })}
                          />
                          {cancelError ? (
                            <p role="alert" className="text-xs text-[var(--color-danger)]">
                              {cancelError}
                            </p>
                          ) : null}
                        </div>
                        <DialogFooter>
                          <DialogClose asChild>
                            <Button variant="ghost">
                              {intl.formatMessage({
                                id: 'invoices.detail.cancel.dismiss',
                                defaultMessage: 'Keep invoice',
                              })}
                            </Button>
                          </DialogClose>
                          <Button
                            variant="destructive"
                            disabled={!cancelReason.trim() || cancelMutation.isPending}
                            onClick={() => {
                              setCancelError(null);
                              cancelMutation.mutate(cancelReason.trim());
                            }}
                          >
                            {cancelMutation.isPending
                              ? intl.formatMessage({
                                  id: 'invoices.detail.cancel.submitting',
                                  defaultMessage: 'Cancelling...',
                                })
                              : intl.formatMessage({
                                  id: 'invoices.detail.cancel.confirm',
                                  defaultMessage: 'Cancel invoice',
                                })}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    {!cancelEnabled ? (
                      <p className="text-xs text-navy-600">
                        {intl.formatMessage({
                          id: 'invoices.detail.actions.cancel.disabledHint',
                          defaultMessage: 'Invoice cannot be cancelled in its current state.',
                        })}
                      </p>
                    ) : null}

                    <div className="border-t border-navy-100 pt-3">
                      {blockDuplicate ? (
                        <Button variant="outline" className="w-full" disabled>
                          <Copy aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                          {intl.formatMessage({
                            id: 'invoices.detail.action.duplicate.button',
                            defaultMessage: 'Duplicate invoice',
                          })}
                        </Button>
                      ) : (
                        <Button asChild variant="outline" className="w-full">
                          <Link to="/invoices/new" search={{ from: invoice.id }}>
                            <Copy aria-hidden className="h-4 w-4" strokeWidth={1.5} />
                            {intl.formatMessage({
                              id: 'invoices.detail.action.duplicate.button',
                              defaultMessage: 'Duplicate invoice',
                            })}
                          </Link>
                        </Button>
                      )}
                      <p className="mt-2 text-xs text-navy-600">
                        {blockDuplicate
                          ? intl.formatMessage({
                              id: 'invoices.detail.action.duplicate.gatedHint',
                              defaultMessage:
                                'Complete KYB and register a PIX payout key to create invoices.',
                            })
                          : intl.formatMessage({
                              id: 'invoices.detail.action.duplicate.hint',
                              defaultMessage:
                                'Create a new draft pre-filled with this buyer and line items.',
                            })}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </section>
            </aside>
          </div>
        </>
      )}
    </section>
  );
}

interface DetailFieldProps {
  label: string;
  children: ReactNode;
}

function DetailField({ label, children }: DetailFieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <p className="text-[11px] font-medium uppercase tracking-wider text-navy-700">{label}</p>
      <p className="text-sm text-navy-900">{children}</p>
    </div>
  );
}

interface TimelineEvent {
  id: string;
  invoiceId: string;
  type: string;
  source: string;
  externalEventId: string | null;
  occurredAt: string;
}

function Timeline({ events }: { events: TimelineEvent[] }): JSX.Element {
  const intl = useIntl();
  if (events.length === 0) {
    return (
      <p className="text-sm text-navy-600">
        {intl.formatMessage({
          id: 'invoices.detail.timeline.empty',
          defaultMessage: 'No events yet.',
        })}
      </p>
    );
  }
  return (
    <ol className="relative space-y-1 border-l border-navy-100 pl-6">
      {events.map((event) => (
        <TimelineRow key={event.id} event={event} />
      ))}
    </ol>
  );
}

type TimelineTone = 'success' | 'danger' | 'pending' | 'neutral';

function TimelineRow({ event }: { event: TimelineEvent }): JSX.Element {
  const intl = useIntl();
  const [expanded, setExpanded] = useState(false);
  const { Icon, tone } = pickIcon(event.type);
  const toneClass = {
    success:
      'border-[var(--color-success)]/30 bg-[var(--color-success)]/10 text-[var(--color-success)]',
    danger:
      'border-[var(--color-danger)]/30 bg-[var(--color-danger)]/10 text-[var(--color-danger)]',
    pending: 'border-navy-200 bg-white text-navy-600',
    neutral: 'border-navy-200 bg-white text-navy-700',
  }[tone];
  // Backend currently does not surface the payload through the DTO. Once it
  // does, this read will pick it up without further changes here.
  const payload = (event as TimelineEvent & { payload?: unknown }).payload;
  return (
    <li className="relative">
      <span
        className={`absolute -left-[34px] top-1.5 flex h-6 w-6 items-center justify-center rounded-full border ${toneClass}`}
      >
        <Icon aria-hidden className="h-3.5 w-3.5" strokeWidth={1.5} />
      </span>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-start gap-2 rounded-md px-2 py-2 text-left transition-colors hover:bg-beige-50/60"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown
            aria-hidden
            className="mt-0.5 h-4 w-4 shrink-0 text-navy-600"
            strokeWidth={1.5}
          />
        ) : (
          <ChevronRight
            aria-hidden
            className="mt-0.5 h-4 w-4 shrink-0 text-navy-600"
            strokeWidth={1.5}
          />
        )}
        <span className="flex-1">
          <span className="block font-mono text-sm font-medium text-navy-900">{event.type}</span>
          <span className="mt-0.5 block text-xs text-navy-600">
            <span className="font-medium text-navy-700">{event.source}</span>
            <span className="mx-1.5 text-navy-300">·</span>
            {formatDateTime(event.occurredAt, intl.locale)}
            {event.externalEventId ? (
              <>
                <span className="mx-1.5 text-navy-300">·</span>
                <span className="font-mono">{event.externalEventId}</span>
              </>
            ) : null}
          </span>
        </span>
      </button>
      {expanded ? (
        <pre className="ml-6 mt-2 mb-2 max-h-64 overflow-auto rounded-md border border-navy-100 bg-beige-50 p-3 font-mono text-xs text-navy-900">
          {JSON.stringify(
            payload !== undefined
              ? payload
              : {
                  id: event.id,
                  type: event.type,
                  source: event.source,
                  externalEventId: event.externalEventId,
                  occurredAt: event.occurredAt,
                },
            null,
            2,
          )}
        </pre>
      ) : null}
    </li>
  );
}

function pickIcon(eventType: string): { Icon: typeof Clock; tone: TimelineTone } {
  if (
    eventType === 'offramp_settled' ||
    eventType === 'buyer_kyc_approved' ||
    eventType === 'fiat_received' ||
    eventType === 'on_chain_confirmed' ||
    eventType === 'settled' ||
    eventType === 'approved'
  ) {
    return { Icon: CircleCheck, tone: 'success' };
  }
  if (
    eventType === 'rejected' ||
    eventType === 'cancelled' ||
    eventType === 'on_ramp_failed' ||
    eventType === 'offramp_failed' ||
    eventType === 'buyer_kyc_rejected'
  ) {
    return { Icon: AlertCircle, tone: 'danger' };
  }
  if (eventType === 'created') {
    return { Icon: CircleDashed, tone: 'pending' };
  }
  return { Icon: Clock, tone: 'neutral' };
}

function formatDateTime(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleString(locale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Date(iso).toLocaleDateString(locale, {
      dateStyle: 'medium',
    });
  } catch {
    return iso;
  }
}
